<template>
  <div class="d-flex justify-content-center align-items-center vh-100 bg-light">
    <div class="vpn-box text-center">
      <div class="vpn-icon mb-3">
        <div class="logo-container">
          <!-- Azure Logo -->
          <img :src="azureLogo" alt="Azure Logo" width="60" class="azure-logo" />

          <!-- Path dengan titik-titik -->
          <div class="path-container">
            <div class="dot" :class="{ active: activeDot >= 1 }"></div>
            <div class="dot" :class="{ active: activeDot >= 2 }"></div>
            <div class="dot" :class="{ active: activeDot >= 3 }"></div>
            <div class="dot" :class="{ active: activeDot >= 4 }"></div>
            <div class="dot" :class="{ active: activeDot >= 5 }"></div>
          </div>

          <!-- Company Logo (berdasarkan SSO login yang dipilih) -->
          <img :src="companyLogo" :alt="companyLabel" width="60" class="hcml-logo" />
        </div>
      </div>
      <h3 class="vpn-title mb-2">You are redirecting to SmartCat Home</h3>
      <div class="progress-text mt-3">
        <span class="loading-text">{{ loadingText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import authService from "@/services/authService";
import menuService from "@/services/generalSettings/menuService";

const body = document.getElementsByTagName("body")[0];
body.classList.remove("bg-gray-100");
export default {
  name: 'AzureRedirect',
  data() {
    return {
      activeDot: 0,
      loadingText: 'Connecting...',
      azureLogo: `${process.env.BASE_URL || "/"}azure-logo.png`,
      // Sama seperti di Login.vue, mapping tipe SSO ke logo & label
      ssoCompanies: [
        { type: "mind-id", label: "MIND ID", logo: `${process.env.BASE_URL || "/"}mind-id.png` },
        { type: "inalum", label: "Inalum", logo: `${process.env.BASE_URL || "/"}inalum.png` },
        { type: "antam", label: "Antam", logo: `${process.env.BASE_URL || "/"}antam.png` },
        { type: "timah", label: "Timah", logo: `${process.env.BASE_URL || "/"}timah.png` },
      ],
    }
  },
  computed: {
    selectedCompany() {
      const type = localStorage.getItem("ssoType");
      return this.ssoCompanies.find((c) => c.type === type) || null;
    },
    companyLogo() {
      return this.selectedCompany
        ? this.selectedCompany.logo
        : `${process.env.BASE_URL || "/"}mind-id.png`;
    },
    companyLabel() {
      return this.selectedCompany ? this.selectedCompany.label : "Company Logo";
    },
  },
  async mounted() {
    this.$store.commit("argonConfig/toggleDefaultLayout");
    // Animasi titik-titik bergerak
    this.dotInterval = setInterval(() => {
      if (this.activeDot < 5) {
        this.activeDot++;
      } else {
        this.activeDot = 0;
      }
    }, 400);

    setTimeout(() => { this.loadingText = 'Authenticating...'; }, 1000);
    setTimeout(() => { this.loadingText = 'Almost there...'; }, 1600);

    await this.handleCallback();
  },
  beforeUnmount() {
    clearInterval(this.dotInterval);
  },
  methods: {
    async handleCallback() {
      const code = this.$route.query?.code || "";
      const temp = this.$route.query?.temp || "";

      // Flag: lindungi interceptor dari clearing auth selama callback masih berlangsung
      sessionStorage.setItem("ssoCallbackActive", "1");

      try {
        let jwtToken;
        let companyType = "sso";

        if (code) {
          companyType = localStorage.getItem("ssoType");
          if (!companyType) {
            this.$toastNotifPrime('error', 'Error', "Company type not found");
            clearInterval(this.dotInterval);
            return;
          }
          const resp = await authService.convertSsoToken(companyType, code);
          jwtToken = resp?.data?.data;
          if (!jwtToken) {
            throw new Error("Token not found in response");
          }
          localStorage.removeItem("ssoType");
        } else if (temp) {
          jwtToken = temp;
        } else {
          this.$toastNotifPrime('error', 'Error', "There is no credential key");
          clearInterval(this.dotInterval);
          return;
        }

        let payload = jwtToken.user || {};
        if (!payload.id && jwtToken.token) {
          try {
            payload = JSON.parse(
              atob(jwtToken.token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
            );
          } catch {
            payload = {};
          }
        }
        
        this.$store.dispatch("auth/setUser", {
          id: payload.id || payload.sub,
          namecode: payload.namecode,
          name: payload.name,
          username: payload.username,
          client_code: payload.client_code,
          company_code: payload.company_code,
          // ANTAM: DistrictCode dari BE (convertToken); company lain null.
          district_code: payload.district_code ?? null,
        });

        const roles = jwtToken.roles || [];
        const permissions = jwtToken.permissions || [];
        this.$store.dispatch("auth/setUserRolesPermissions", { roles, permissions });

        localStorage.removeItem('token');
        await this.$store.dispatch("auth/login", jwtToken);

        await this.setMenuSubMenu();
        this.$store.commit("auth/setSelectedModule", "Material Management");
        window.location.href = this.getRedirectTarget();
      } catch (err) {
        clearInterval(this.dotInterval);
        try { sessionStorage.removeItem("ssoCallbackActive"); } catch (e) { /* ignore */ }
        this.$toastNotifPrime('error', 'Error', "Login failed: " + (err?.message || err || "Unknown error"));
      }
    },
    getRedirectTarget() {
      const redirect = localStorage.getItem("ssoRedirect");
      localStorage.removeItem("ssoRedirect");
      const isSafeRedirect = typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//") && !redirect.includes("://");
      return isSafeRedirect ? redirect : "/material-management/dashboards/my-task";
    },
    async setMenuSubMenu() {
      try {
        const responseMenu = await menuService.getManageMenu();
        const menus = responseMenu.data.data;

        const menuNav = menus.reduce((acc, menu) => {
          acc[menu.name] = { status: menu.status === "active" };
          return acc;
        }, {});
        const subMenuNav = menus.reduce((acc, menu) => {
          menu.sub_menu.forEach((sub) => {
            acc[sub.name] = { status: sub.status === "active" };
          });
          return acc;
        }, {});
        this.$store.dispatch("auth/setMenuNav", menuNav);
        this.$store.dispatch("auth/setSubMenuNav", subMenuNav);
      } catch {
        this.$store.dispatch("auth/setMenuNav", []);
        this.$store.dispatch("auth/setSubMenuNav", []);
      }
    },
  },
}
</script>

<style scoped>
.vpn-box {
  background-color: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

.vpn-title {
  font-size: 24px;
  font-weight: 600;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.azure-logo, .hcml-logo {
  flex-shrink: 0;
}

.path-container {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 15px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #e0e0e0;
  transition: all 0.3s ease;
}

.dot.active {
  background-color: #007bff;
  transform: scale(1.2);
  box-shadow: 0 0 8px rgba(0, 123, 255, 0.5);
}

.progress-text {
  margin-top: 20px;
}

.loading-text {
  font-size: 16px;
  color: #007bff;
  font-weight: 500;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

/* Tambahan animasi untuk logo Azure */
.azure-logo {
  animation: glow 2s infinite alternate;
}

@keyframes glow {
  from {
    filter: brightness(1);
  }
  to {
    filter: brightness(1.1) drop-shadow(0 0 5px rgba(0, 123, 255, 0.3));
  }
}
</style>